import { css } from '@emotion/css';
import { TFunction } from 'i18next';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../features';
import { actions } from '../../features/myDesign';
import { throttle } from '../../lib/utils';
import { ConfirmButton } from '../shared/buttons/ConfirmButton';
import { NormalButton } from '../shared/buttons/NormalButton';
import { DialogRoute } from '../shared/routes/DialogRoute';
import { MyDesignErrorDialog } from './MyDesignErrorDialog';
import { MyDesignPreview } from './MyDesignPreview';

const confirmDialogStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;

  .content {
    width: 100%;
    max-width: 480px;
    max-height: 640px;
    color: #4d3906;
    text-align: center;
    padding: 32px 20px;
  }
  .buttons {
    width: 100%;
  }
  .buttons > button {
    margin: 12px 0 0;
  }
`;

export const ConfirmMyDesign: React.FC = () => {
  const { t } = useTranslation(['my_design', 'translation']);
  const history = useHistory();
  const { readMyDesigns, uploadStatus, uploadedMyDesign } = useSelector(
    (state: RootState) => state.myDesign
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(actions.invalidateMyDesign());
    return () => {
      dispatch(actions.discardMyDesign());
    };
  }, [dispatch]);

  useEffect(() => {
    if (uploadStatus === 'failed') {
      history.push('/players/confirm-my-design/error');
    }
  }, [uploadStatus, history]);

  useEffect(() => {
    if (uploadStatus === 'succeeded') {
      history.goBack();
    }
  }, [readMyDesigns, uploadStatus, history]);

  const handleSaveClick = useCallback(() => {
    if (uploadedMyDesign) {
      dispatch(actions.upsertMyDesign());
    } else {
      dispatch(actions.postMyDesign());
    }
  }, [uploadedMyDesign, dispatch]);

  const handleCloseClick = useCallback(
    throttle(() => {
      dispatch(actions.discardMyDesign());
      history.goBack();
    }, 1000),
    [dispatch, history]
  );

  if (!readMyDesigns) {
    return <></>;
  }

  const isPro = readMyDesigns[0].designType === 'pro';
  const hasUploaded = !!uploadedMyDesign;

  const [message, buttonText] = getTexts(isPro, hasUploaded, t);

  const isLoading = uploadStatus === 'loading';
  return (
    <>
      <main className={confirmDialogStyles}>
        <section className="content">
          <p className="message">{message}</p>
          <MyDesignPreview myDesignQRs={readMyDesigns} />
          <div className="buttons">
            <ConfirmButton loading={isLoading} onClick={handleSaveClick}>
              {buttonText}
            </ConfirmButton>
            <NormalButton onClick={handleCloseClick} disabled={isLoading}>
              {t('translation:dialog_button_cancel')}
            </NormalButton>
          </div>
        </section>
      </main>

      <DialogRoute path="/players/confirm-my-design/error">
        <MyDesignErrorDialog />
      </DialogRoute>
    </>
  );
};

// helper functions

function getTexts(isPro: boolean, hasUploaded: boolean, t: TFunction): [string, string] {
  let message: string;
  let buttonText: string;
  if (hasUploaded) {
    if (isPro) {
      message = t('new_pro_design_read_00');
    } else {
      message = t('new_design_read_00');
    }
    buttonText = t('button_overwrite');
  } else {
    if (isPro) {
      message = t('new_pro_design_read_01');
    } else {
      message = t('new_design_read_01');
    }
    buttonText = t('button_save');
  }
  return [message, buttonText];
}
